import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { cn } from '@/utils/cn';

interface PageNavigatorProps {
	currentPage: number;
	totalPages: number;
	setCurrentPage: (page: number) => void;
}

type PageItem = number | 'ellipsis';

export const PageNavigator = ({
	currentPage,
	totalPages,
	setCurrentPage,
}: PageNavigatorProps) => {
	const generatePages = (): PageItem[] => {
		const firstPage = 1;
		const lastPage = totalPages;
		const previousPage = currentPage - 1;
		const nextPage = currentPage + 1;

		const pageNumbers = new Set<number>();

		pageNumbers.add(firstPage);

		if (previousPage >= firstPage) {
			pageNumbers.add(previousPage);
		}

		pageNumbers.add(currentPage);

		if (nextPage <= lastPage) {
			pageNumbers.add(nextPage);
		}

		pageNumbers.add(lastPage);

		const sortedPages = Array.from(pageNumbers).sort((a, b) => a - b);

		const result: PageItem[] = [];

		for (let i = 0; i < sortedPages.length; i++) {
			const current = sortedPages[i];
			const next = sortedPages[i + 1];

			result.push(current);

			if (next !== undefined && next - current > 1) {
				if (
					!(current === firstPage && next === firstPage + 1) &&
					!(next === lastPage && current === lastPage - 1)
				) {
					result.push('ellipsis');
				}
			}
		}

		return result;
	};

	const pages = generatePages();

	const handlePrevious = () => {
		if (currentPage > 1) {
			setCurrentPage(currentPage - 1);
		}
	};

	const handleNext = () => {
		if (currentPage < totalPages) {
			setCurrentPage(currentPage + 1);
		}
	};

	return (
		<div className="flex flex-row justify-end items-center pb-2 gap-2 h-10">
			<button
				onClick={handlePrevious}
				disabled={currentPage === 1}
				type="button"
				className={cn(
					'w-6 h-6 rounded-md flex items-center justify-center border-[1px] border-gray-500 text-primary text-xs font-semibold',
					currentPage === 1
						? 'bg-gray-200 text-gray-400 cursor-not-allowed'
						: 'bg-white hover:bg-gray-50',
				)}
			>
				<MdKeyboardArrowLeft className="w-5 h-5" />
			</button>

			{pages.map((page, index) => {
				if (page === 'ellipsis') {
					return (
						<span
							key={`ellipsis-${
								// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
								index
							}`}
							className="w-6 h-6 rounded-md flex items-center justify-center text-gray-500 text-xs font-semibold"
						>
							...
						</span>
					);
				}

				return (
					<button
						key={page}
						onClick={() => setCurrentPage(page)}
						type="button"
						className={cn(
							'w-6 h-6 rounded-md flex items-center justify-center border-[1px] border-gray-500 text-primary text-xs font-semibold',
							currentPage === page
								? 'bg-primary/80 text-white cursor-default'
								: 'bg-white hover:bg-gray-50',
						)}
					>
						{page}
					</button>
				);
			})}

			<button
				onClick={handleNext}
				disabled={currentPage === totalPages}
				type="button"
				className={cn(
					'w-6 h-6 rounded-md flex items-center justify-center border-[1px] border-gray-500 text-primary text-xs font-semibold',
					currentPage === totalPages
						? 'bg-gray-200 text-gray-400 cursor-not-allowed'
						: 'bg-white hover:bg-gray-50',
				)}
			>
				<MdKeyboardArrowRight className="w-5 h-5" />
			</button>
		</div>
	);
};
